/**
 * Helper functions để lưu và lấy search parameters từ localStorage
 * Sử dụng cho các màn hình cần lưu trạng thái tìm kiếm khi reload trang
 */

/**
 * Prefix cho localStorage keys để tránh conflict
 */
const STORAGE_PREFIX = 'search_params_';

/**
 * Lưu search parameters vào localStorage
 * @param screenKey - Key định danh màn hình (ví dụ: 'wasteHandoverPlan', 'purchaseOrder')
 * @param params - Object chứa các tham số search cần lưu
 *
 * @example
 * saveSearchParams('wasteHandoverPlan', {
 *   filters: { deliveryDateFrom: '2024-01-01', vendorNumber: 'V001' },
 *   paging: { pageIndex: 1, pageSize: 10 }
 * });
 */
export const saveSearchParams = <T = any>(screenKey: string, params: T): void => {
  try {
    const storageKey = `${STORAGE_PREFIX}${screenKey}`;
    localStorage.setItem(storageKey, JSON.stringify(params));
  } catch (error) {
    console.error(`Error saving search params for ${screenKey}:`, error);
  }
};

/**
 * Lấy search parameters từ localStorage
 * @param screenKey - Key định danh màn hình
 * @param defaultParams - Giá trị mặc định nếu không tìm thấy trong localStorage
 * @returns Object chứa search parameters hoặc defaultParams nếu không tìm thấy
 *
 * @example
 * const savedParams = getSearchParams('wasteHandoverPlan', initSearch);
 * if (savedParams) {
 *   setDataSearch(savedParams);
 * }
 */
export const getSearchParams = <T = any>(screenKey: string, defaultParams?: T): T | null => {
  try {
    const storageKey = `${STORAGE_PREFIX}${screenKey}`;
    const savedData = localStorage.getItem(storageKey);

    if (savedData) {
      const parsedData = JSON.parse(savedData);
      // Merge với defaultParams để đảm bảo có đầy đủ các field cần thiết
      return defaultParams ? { ...defaultParams, ...parsedData } : parsedData;
    }

    return defaultParams || null;
  } catch (error) {
    console.error(`Error getting search params for ${screenKey}:`, error);
    return defaultParams || null;
  }
};

/**
 * Xóa search parameters khỏi localStorage
 * @param screenKey - Key định danh màn hình
 *
 * @example
 * clearSearchParams('wasteHandoverPlan');
 */
export const clearSearchParams = (screenKey: string): void => {
  try {
    const storageKey = `${STORAGE_PREFIX}${screenKey}`;
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(`Error clearing search params for ${screenKey}:`, error);
  }
};

/**
 * Kiểm tra xem có search parameters được lưu hay không
 * @param screenKey - Key định danh màn hình
 * @returns true nếu có saved params, false nếu không
 *
 * @example
 * if (hasSearchParams('wasteHandoverPlan')) {
 *   const params = getSearchParams('wasteHandoverPlan');
 * }
 */
export const hasSearchParams = (screenKey: string): boolean => {
  try {
    const storageKey = `${STORAGE_PREFIX}${screenKey}`;
    return localStorage.getItem(storageKey) !== null;
  } catch (error) {
    console.error(`Error checking search params for ${screenKey}:`, error);
    return false;
  }
};

/**
 * Cập nhật một phần của search parameters (merge với dữ liệu cũ)
 * @param screenKey - Key định danh màn hình
 * @param partialParams - Object chứa các tham số cần cập nhật
 *
 * @example
 * updateSearchParams('wasteHandoverPlan', {
 *   filters: { vendorNumber: 'V002' }
 * }); // Chỉ cập nhật vendorNumber, các field khác giữ nguyên
 */
export const updateSearchParams = <T = any>(screenKey: string, partialParams: Partial<T>): void => {
  try {
    const currentParams = getSearchParams<T>(screenKey);
    if (currentParams) {
      const updatedParams = { ...currentParams, ...partialParams };
      saveSearchParams(screenKey, updatedParams);
    } else {
      saveSearchParams(screenKey, partialParams);
    }
  } catch (error) {
    console.error(`Error updating search params for ${screenKey}:`, error);
  }
};
